import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';

interface CarouselWrapperProps<T> {
  items: T[];
  renderCard: (item: T, index: number) => React.ReactNode;
  autoRotate?: boolean;
}

const CarouselWrapper = <T,>({ items, renderCard, autoRotate = true }: CarouselWrapperProps<T>) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);
  const totalSlides = items.length;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setUserInteracted(false);
    }, 30000);
  };

  useEffect(() => {
    if (autoRotate && totalSlides > 0 && !userInteracted) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [totalSlides, userInteracted, autoRotate]);

  useEffect(() => {
    resetTimeout();
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [userInteracted]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
    setUserInteracted(true);
    resetTimeout();
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    setUserInteracted(true);
    resetTimeout();
  };

  return (
    <div className="relative w-full">
      <div className="flex flex-col justify-center px-0 max-sm:mt-5 sm:px-16 xl:flex-row">
        <div className="mx-auto max-w-3xl flex-1 p-0 sm:p-5">{renderCard(items[currentSlide], currentSlide)}</div>
        {totalSlides > 1 && (
          <div className="hidden max-w-3xl flex-1 p-5 xl:block">
            {renderCard(items[(currentSlide + 1) % totalSlides], (currentSlide + 1) % totalSlides)}
          </div>
        )}
      </div>

      {/* Navigation arrows for larger screens */}
      <div className="absolute left-0 right-0 top-1/2 flex -translate-y-1/2 transform justify-between max-sm:hidden">
        <button className="btn btn-circle" onClick={prevSlide}>
          <FontAwesomeIcon icon={faChevronLeft} className="h-5 w-5 text-primary" />
        </button>
        <button className="btn btn-circle" onClick={nextSlide}>
          <FontAwesomeIcon icon={faChevronRight} className="h-5 w-5 text-primary" />
        </button>
      </div>

      {/* Navigation arrows for smaller screens */}
      <div className="mt-5 flex items-center justify-center space-x-8 sm:hidden">
        <button className="btn btn-circle" onClick={prevSlide}>
          <FontAwesomeIcon icon={faChevronLeft} className="h-5 w-5 text-primary" />
        </button>
        <button className="btn btn-circle" onClick={nextSlide}>
          <FontAwesomeIcon icon={faChevronRight} className="h-5 w-5 text-primary" />
        </button>
      </div>
    </div>
  );
};

export default CarouselWrapper;
